import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import MainCard from 'components/MainCard';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import Swal from 'sweetalert2';
import { TailSpin } from 'react-loader-spinner';
import { MdCloudUpload } from 'react-icons/md';
import { Edit, Trash } from 'lucide-react';
import DataTable from 'react-data-table-component';
import { ToastContainer, toast } from 'react-toastify';
import { FiSearch } from 'react-icons/fi';
import BackButton from 'components/BackButton';
import { baseURLFinanceVendor } from 'config/apiConfig';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { FcClearFilters } from "react-icons/fc";
import { customStyles } from 'utils/customStyleDataTable';

Modal.setAppElement('#root');

const ExcessFeeSlabs = () => {
    const [file, setFile] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const [excessFeesSlabsData, setExcessFeeSlabsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showUploader, setShowUploader] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [selectedClient, setSelectedClient] = useState('');
    const [selectedPlan, setSelectedPlan] = useState('');
    const [selectedCalcType, setSelectedCalcType] = useState('');


    const uniqueClients = useMemo(() => {
        return [...new Set(excessFeesSlabsData.map(item => item.client).filter(Boolean))];
    }, [excessFeesSlabsData]);

    const uniquePlans = useMemo(() => {
        return [...new Set(excessFeesSlabsData.map(item => item.plan).filter(Boolean))];
    }, [excessFeesSlabsData]);

    const calcTypes = ['Percentage', 'Fixed Amount'];

    const isFilterActive =
        selectedClient || selectedPlan || selectedCalcType || searchTerm;

    const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        'application/vnd.ms-excel.template.macroEnabled.12',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
        'text/csv',
    ];

    const maxSize = 10 * 1024 * 1024;

    const handleFile = (selectedFile) => {
        if (!selectedFile) return;

        if (!allowedTypes.includes(selectedFile.type)) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File Type',
                text: 'Please upload a valid Excel file (.xls, .xlsx, .xlsm, .xlsb, .xltx, .xltm, .csv)',
            });
            return;
        }

        if (selectedFile.size > maxSize) {
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'File size should not exceed 10MB!',
            });
            return;
        }

        setFile(selectedFile);
    };

    const handleExcelUploadChange = (event) => {
        const selectedFile = event.target.files[0];
        handleFile(selectedFile);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFile = event.dataTransfer.files[0];
        handleFile(droppedFile);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const fetchUploadedExcessFeeSlabs = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem("token");

            const res = await axios.get(
                `${baseURLFinanceVendor}getExcessFeesSlabs`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (res && res.data) {
                setExcessFeeSlabsData(res?.data?.data || []);
            }
        } catch (error) {
            toast.error('Error while fetching data!');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUploadedExcessFeeSlabs();
    }, []);

    const handleExcelUpload = async () => {
        if (!file) {
            Swal.fire({
                icon: 'warning',
                title: 'No File Selected',
                text: 'Please select a file before uploading!',
            });
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setLoadingData(true);

        try {
            const token = localStorage.getItem("token");

            const response = await axios.post(
                `${baseURLFinanceVendor}uploadExcessFeesCalAmountSlabs`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response) {
                await fetchUploadedExcessFeeSlabs();
                setShowUploader(false);
                Swal.fire({
                    icon: 'success',
                    title: 'Upload Successful',
                    text: response.data.message || 'Excess Fees Slabs uploaded successfully!',
                    confirmButtonText: 'OK',
                });

                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: error.response?.data?.message || 'Something went wrong. Please try again!',
            });
        } finally {
            setLoadingData(false);
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = '/templates/Excess_Fees_Cal_Amount_Slabs.xlsx';
        link.download = 'Excess_Fees_Cal_Amount_Slabs_Template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleEdit = useCallback((row) => {
        let displayValue = row.percentageOrBaseAmount || 0;

        if (row.calculationType === 'Percentage') {
            displayValue = Number(row.percentageOrBaseAmount) * 100;
        }

        setEditFormData({
            id: row.id,
            client: row.client || '',
            plan: row.plan || '',
            slabStart: row.slabStart || 0,
            slabEnd: row.slabEnd || 0,
            calculationType: row.calculationType || 'Percentage',
            percentageOrBaseAmount: displayValue,
            parentValue: row.parentValue || '',
            type: 'edit'
        });
        setShowEditModal(true);
        setShowAddModal(false);
    }, []);

    const handleAdd = useCallback(() => {
        setEditFormData({
            id: 0,
            client: '',
            plan: '',
            slabStart: 0,
            slabEnd: 0,
            calculationType: 'Percentage',
            percentageOrBaseAmount: 0,
            parentValue: '',
            type: 'add'
        });
        setShowAddModal(true);
        setShowEditModal(false);
    }, []);

    const handleEditChange = useCallback((e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value === '' ? ''
                : ['slabStart', 'slabEnd', 'percentageOrBaseAmount'].includes(name)
                    ? parseFloat(value) || 0
                    : value,
        }));
    }, []);

    const handleSaveEdit = async () => {
        if (!editFormData) return;

        let payload = { ...editFormData };

        if (payload.calculationType === 'Percentage') {
            payload.percentageOrBaseAmount = (Number(payload.percentageOrBaseAmount) / 100).toString();
        } else {
            payload.percentageOrBaseAmount = payload.percentageOrBaseAmount.toString();
        }

        payload = {
            id: Number(payload.id),
            client: payload.client || "",
            plan: payload.plan || "",
            slabStart: Number(payload.slabStart),
            slabEnd: Number(payload.slabEnd),
            calculationType: payload.calculationType || "Percentage",
            percentageOrBaseAmount: payload.percentageOrBaseAmount,
            parentValue: payload.parentValue || "",
        };

        setLoadingData(true);

        try {
            const token = localStorage.getItem("token");

            const res = await axios.post(
                `${baseURLFinanceVendor}editExcessFeesSlabs`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.status === 200 || res.status === 201) {
                await fetchUploadedExcessFeeSlabs();
                setShowEditModal(false);
                setEditFormData(null);
                Swal.fire({
                    icon: 'success',
                    title: 'Update Successful',
                    text: 'Excess Fees Slab updated successfully!',
                });
            }
        } catch (error) {
            console.error("Edit Error:", error.response?.data);
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: error.response?.data?.errors?.model?.[0] ||
                    error.response?.data?.title ||
                    'Something went wrong!',
            });
        } finally {
            setLoadingData(false);
        }
    };
    const handleSaveAdd = async () => {
        if (!editFormData) return;

        let payload = { ...editFormData };

        if (payload.calculationType === 'Percentage') {
            payload.percentageOrBaseAmount = (Number(payload.percentageOrBaseAmount) / 100).toString();
        } else {
            payload.percentageOrBaseAmount = payload.percentageOrBaseAmount.toString();
        }

        payload = {
            id: 0,
            client: payload.client || "",
            plan: payload.plan || "",
            slabStart: Number(payload.slabStart),
            slabEnd: Number(payload.slabEnd),
            calculationType: payload.calculationType || "Percentage",
            percentageOrBaseAmount: payload.percentageOrBaseAmount,
            parentValue: payload.parentValue || "",
        };

        setLoadingData(true);

        try {
            const token = localStorage.getItem("token");

            const res = await axios.post(
                `${baseURLFinanceVendor}addExcessFeesSlabs`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            if (res.status === 200 || res.status === 201) {
                await fetchUploadedExcessFeeSlabs();
                setShowAddModal(false);
                setEditFormData(null);
                Swal.fire({
                    icon: 'success',
                    title: 'Add Successful',
                    text: 'Excess Fee Slab added successfully!',
                });
            }
        } catch (error) {
            console.error("Add Error:", error.response?.data);
            Swal.fire({
                icon: 'error',
                title: 'Add Failed',
                text: error.response?.data?.title || 'Something went wrong!',
            });
        } finally {
            setLoadingData(false);
        }
    };

    const handleCancelEdit = useCallback(() => {
        setShowEditModal(false);
        setShowAddModal(false);
        setEditFormData(null);
    }, []);

    const handleDelete = async (row) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoadingData(true);

                    const token = localStorage.getItem("token");

                    await axios.post(
                        `${baseURLFinanceVendor}deleteExcessFeesSlabs?id=${row.id}`,
                        null,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    await fetchUploadedExcessFeeSlabs();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Record deleted successfully',
                    });

                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Delete Failed',
                        text: error.response?.data?.message || 'Something went wrong!',
                    });
                } finally {
                    setLoadingData(false);
                }
            }
        });
    };

    const columns = useMemo(
        () => [
            {
                name: 'SN',
                selector: (row, index) => (currentPage - 1) * rowsPerPage + (index + 1),
                width: '60px',
            },
            {
                name: 'Client',
                selector: (row) => row.client || '',
                sortable: true,
                grow: 1.2

            },
            {
                name: 'Plan',
                selector: (row) => row.plan || '',
                sortable: true,
                grow: 2
            },
            {
                name: 'Parent Value',
                selector: (row) => row.parentValue || '-',
                sortable: true,
                grow: 1.3
            },
            {
                name: 'Slab Start',
                selector: (row) => row.slabStart ?? '',
                sortable: true,
                grow: 0.8
            },
            {
                name: 'Slab End',
                selector: (row) => row.slabEnd ?? '',
                sortable: true,
                grow: 0.8
            },
            {
                name: 'Calculation Type',
                selector: (row) => row.calculationType || '',
                sortable: true,
                grow: 1.2
            },
            {
                name: 'Value',
                selector: (row) =>
                    row.calculationType === 'Percentage'
                        ? `${Number(row.percentageOrBaseAmount) * 100}%`
                        : row.percentageOrBaseAmount,
                sortable: true,
                grow: 0.6
            },
            {
                name: 'Actions',
                cell: (row) => (
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleEdit(row)}
                            className="p-2 bg-purple-600 text-white rounded-full"
                        >
                            <Edit className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => handleDelete(row)}
                            className="p-2 bg-red-500 text-white rounded-full"
                        >
                            <Trash className="w-5 h-5" />
                        </button>
                    </div>
                ),
            },
        ],
        [currentPage, rowsPerPage]
    );

    const filteredData = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();

        return excessFeesSlabsData.filter((slab) => {
            const matchesSearch =
                slab.client?.toLowerCase().includes(searchLower) ||
                slab.plan?.toLowerCase().includes(searchLower) ||
                slab.slabStart?.toString().includes(searchLower) ||
                slab.slabEnd?.toString().includes(searchLower) ||
                slab.calculationType?.toLowerCase().includes(searchLower) ||
                slab.parentValue?.toLowerCase().includes(searchLower) ||
                (slab.calculationType === "Percentage" &&
                    `${Number(slab.percentageOrBaseAmount) * 100}`.includes(searchLower));

            const matchesClient = !selectedClient || slab.client === selectedClient;
            const matchesPlan = !selectedPlan || slab.plan === selectedPlan;
            const matchesCalcType = !selectedCalcType || slab.calculationType === selectedCalcType;

            return matchesSearch && matchesClient && matchesPlan && matchesCalcType;
        });
    }, [excessFeesSlabsData, searchTerm, selectedClient, selectedPlan, selectedCalcType]);

    const modalStyles = {
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 50,
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            margin: '16px',
        },
    };


    return (
        <div>
            <MainCard>
                <div className="flex justify-between gap-4">
                    <BackButton />
                    <div className="flex justify-end items-center gap-[1.5rem]">
                        <button
                            onClick={handleDownload}
                            className="flex items-center px-4 py-2 border border-purple-700 text-purple-700 rounded-lg 
                                   hover:bg-purple-50 transition-all duration-300"
                        >
                            <FileDownloadOutlinedIcon className="mr-2" /> Download Template
                        </button>
                        <button
                            onClick={() => setShowUploader(!showUploader)}
                            className="flex items-center px-4 py-2 bg-purple-700 text-white rounded-lg 
                                   hover:bg-purple-800 transition-all duration-300"
                        >
                            <CloudUploadIcon className="mr-2" /> {showUploader ? 'Hide Uploader' : 'Show Uploader'}
                        </button>
                        <button
                            onClick={handleAdd}
                            className="hidden flex items-center px-4 py-2 bg-green-600 text-white rounded-lg 
                                   hover:bg-green-700 transition-all duration-300"
                        >
                            <AddIcon className="mr-2" /> Add New Excess Fee Slab
                        </button>
                    </div>
                </div>

                {showUploader && (
                    <div
                        className={`flex flex-col items-center justify-center pb-4 sm:pb-4 
                                    border-2 border-dashed rounded-xl transition-all duration-300 text-center
                                    ${isDragging ? 'border-purple-800 bg-purple-200' : 'border-purple-600 bg-purple-50 hover:bg-purple-100'}`}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <label
                            htmlFor="file-upload"
                            className="flex flex-col items-center cursor-pointer text-purple-700 hover:text-purple-800 transition-all"
                        >
                            <MdCloudUpload size={60} className="mb-2 hover:scale-110 transition-transform duration-300" />
                            <span className="text-base sm:text-lg font-semibold">
                                {isDragging ? 'Drop File Here' : 'Choose File'}
                            </span>
                            <span className="text-xs text-gray-500">(Excel or CSV - .xls, .xlsx, .csv)</span>
                        </label>

                        <input
                            id="file-upload"
                            type="file"
                            accept=".xls,.xlsx,.xlsm,.xlsb,.xlt,.xltx,.xltm,.csv"
                            onChange={handleExcelUploadChange}
                            className="hidden"
                            ref={fileInputRef}
                        />

                        {file && (
                            <p className="text-sm text-gray-700 mt-4 break-words px-2">
                                <span className="font-medium text-purple-700">Selected:</span> {file.name}
                            </p>
                        )}

                        <button
                            onClick={handleExcelUpload}
                            disabled={loadingData}
                            className={`mt-5 flex items-center px-6 py-2 rounded-lg font-medium transition-all duration-300 shadow-md
                                        ${loadingData ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-700 hover:bg-purple-800 text-white'}`}
                        >
                            <CloudUploadIcon className="mr-2" /> {loadingData ? 'Uploading...' : 'Upload BER Slabs'}
                        </button>
                    </div>
                )}

                <div className='flex justify-between items-center gap-3'>
                    <div className="flex flex-wrap items-center gap-4 my-[0.5rem]">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by Client, Plan, Slab, Type, Value, Parent"
                                className="peer pl-10 pr-4 py-2 w-80 md:w-96 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#6C5DD3]"
                            />
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 peer-focus:text-[#6C5DD3]">
                                <FiSearch className="" />
                            </span>
                        </div>
                    </div>

                    <div className='flex justify-start gap-3 items-center'>
                        <div className='flex justify-start gap-2 items-center'>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Client</InputLabel>
                                <Select
                                    value={selectedClient}
                                    label="Client"
                                    onChange={(e) => setSelectedClient(e.target.value)}
                                >
                                    <MenuItem value="">All Clients</MenuItem>
                                    {uniqueClients.map(client => (
                                        <MenuItem key={client} value={client}>{client}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Plan Filter */}
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Plan</InputLabel>
                                <Select
                                    value={selectedPlan}
                                    label="Plan"
                                    onChange={(e) => setSelectedPlan(e.target.value)}
                                >
                                    <MenuItem value="">All Plans</MenuItem>
                                    {uniquePlans.map(plan => (
                                        <MenuItem key={plan} value={plan}>{plan}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Calculation Type Filter */}
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Calc Type</InputLabel>
                                <Select
                                    value={selectedCalcType}
                                    label="Calc Type"
                                    onChange={(e) => setSelectedCalcType(e.target.value)}
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {calcTypes?.map(ct => (
                                        <MenuItem key={ct} value={ct}>{ct}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        <button
                            onClick={() => {
                                setSelectedClient('');
                                setSelectedPlan('');
                                setSelectedCalcType('');
                                setSearchTerm('');
                            }}
                            disabled={!isFilterActive}
                            className={`p-[0.3rem] text-[1.4rem] border rounded transition-all duration-300 ease-in-out
        ${isFilterActive
                                    ? 'text-gray-600 border-gray-300 hover:text-red-600 hover:bg-gray-200 hover:scale-110 cursor-pointer'
                                    : 'text-gray-300 border-gray-200 cursor-not-allowed'
                                }`}
                            title="Reset Filters"
                        >
                            <FcClearFilters />
                        </button>
                    </div>
                </div>


                <div className="overflow-auto rounded-[0.3rem] border border-gray-200 shadow-sm ">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-[300px] gap-3">
                            <TailSpin
                                visible={true}
                                height="50"
                                width="50"
                                color="#7E22CE"
                                ariaLabel="tail-spin-loading"
                                radius="1"
                            />

                            <p className="text-sm font-medium text-gray-600">
                                Fetching excess fees data...
                            </p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            customStyles={customStyles}
                            pagination
                            paginationServer={false}
                            paginationTotalRows={filteredData.length}
                            highlightOnHover
                            responsive
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setRowsPerPage(newPerPage);
                                setCurrentPage(page);
                            }}
                        // customStyles={{
                        //     headCells: {
                        //         style: {
                        //             backgroundColor: '#f5f5f5',
                        //             fontWeight: 'bold',
                        //             textAlign: 'left',
                        //         },
                        //     },
                        // }}
                        />

                    )}
                </div>

                {loadingData && (
                    <div className="fixed inset-0 bg-white bg-opacity-80 z-999 flex justify-center items-center">
                        <TailSpin height="60" width="60" color="#6C5DD3" ariaLabel="loading" />
                    </div>
                )}
            </MainCard>

            <Modal
                isOpen={showEditModal || showAddModal}
                onRequestClose={handleCancelEdit}
                style={modalStyles}
                contentLabel={showEditModal ? 'Edit Excess Fee Slab' : 'Add New Excess Fee Slab'}
            >
                {editFormData && (
                    <div>
                        <h2 className="text-xl font-bold mb-4 text-purple-700">
                            {showEditModal ? 'Edit Excess Fee Slab' : 'Add New Excess Fee Slab'}
                        </h2>

                        <form className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Client</label>
                                <input
                                    type="text"
                                    name="client"
                                    value={editFormData.client}
                                    onChange={handleEditChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Plan</label>
                                <input
                                    type="text"
                                    name="plan"
                                    value={editFormData.plan}
                                    onChange={handleEditChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Slab Start</label>
                                    <input
                                        type="number"
                                        name="slabStart"
                                        value={editFormData.slabStart}
                                        onChange={handleEditChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Slab End</label>
                                    <input
                                        type="number"
                                        name="slabEnd"
                                        value={editFormData.slabEnd}
                                        onChange={handleEditChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Calculation Type</label>
                                <select
                                    name="calculationType"
                                    value={editFormData.calculationType}
                                    onChange={handleEditChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                >
                                    <option value="Percentage">Percentage</option>
                                    <option value="Fixed">Fixed Amount</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {editFormData.calculationType === 'Percentage'
                                        ? 'Percentage (%)'
                                        : 'Fixed Amount'}
                                </label>
                                <input
                                    type="number"
                                    name="percentageOrBaseAmount"
                                    value={editFormData.percentageOrBaseAmount}
                                    onChange={handleEditChange}
                                    step={editFormData.calculationType === 'Percentage' ? "0.01" : "0.01"}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Parent Value</label>
                                <input
                                    type="text"
                                    name="parentValue"
                                    value={editFormData.parentValue}
                                    onChange={handleEditChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                            </div>
                        </form>

                        <div className="flex justify-end gap-4 mt-6">
                            <button
                                onClick={showEditModal ? handleSaveEdit : handleSaveAdd}
                                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all"
                            >
                                Save
                            </button>
                            <button
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
            <ToastContainer />
        </div>
    );
};

export default ExcessFeeSlabs;